import { Box, Stack } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import Panel from '../../elements/Panel/Panel';
import SidebarLayout from '../../elements/SidebarLayout/SidebarLayout';
import VerticalSplitPane from '../../elements/VerticalSplitPane/VerticalSplitPane';
import { loadLogsAndFilters } from '../actions/audit-logs-actions';
import AuditLogsHeader from '../components/AuditLogsHeader';
import AuditLogsLoadError from '../components/AuditLogsLoadError';
import AuditLogsTable from '../components/AuditLogsTable';
import DatePicker from '../components/DatePicker';
import {
  clearAuditLogs,
  clearDatePicker,
  setDatePickerFrom,
  setDatePickerTo,
  setInitialDatePicker,
} from '../store-actions/audit-logs-store-actions';
import AuditLogsStore from '../stores/audit-logs-store';
import AuditLogsDrawerContainer from './AuditLogsDrawerContainer';
import AuditLogsSidebarContainer from './AuditLogsSidebarContainer';

const AuditLogsContainer: FC<
  React.PropsWithChildren<AuditLogsContainerProps>
> = props => {
  const { isReadOnlyUser } = props;
  const { errorMessage, datePickerFrom, datePickerTo } =
    useSnapshot(AuditLogsStore);

  const [dateChanged, setDateChanged] = useState<boolean>(false);

  useEffect(() => {
    if (isReadOnlyUser) {
      return;
    }

    setInitialDatePicker();
    loadLogsAndFilters();

    return () => {
      clearDatePicker();
      clearAuditLogs();
    };
  }, [isReadOnlyUser]);

  useEffect(() => {
    if (isReadOnlyUser) {
      return;
    }

    if (!dateChanged) {
      return;
    }

    setDateChanged(false);
    loadLogsAndFilters();
  }, [dateChanged, setDateChanged, isReadOnlyUser]);

  if (errorMessage) {
    return <AuditLogsLoadError errorMessage={errorMessage} />;
  }

  if (isReadOnlyUser) {
    return (
      <AuditLogsLoadError
        errorMessage={
          'Your user role (Read Only) prevents you from seeing this screen'
        }
      />
    );
  }

  return (
    <Stack direction='column' height='100%'>
      <Panel
        flexShrink={0}
        mb={2}
        ml={2}
        mr={2}
        display='flex'
        flexDirection='row'
        justifyContent='space-between'>
        <AuditLogsHeader />
        <Box display='flex' justifyContent='end' gap={2} mt={1} mb={1} mr={2}>
          <DatePicker
            title={'From'}
            date={datePickerFrom}
            setDate={setDatePickerFrom}
            setDateChanged={setDateChanged}
            maxDateTime={datePickerTo}
          />
          <DatePicker
            title={'To'}
            date={datePickerTo}
            setDate={setDatePickerTo}
            setDateChanged={setDateChanged}
            minDateTime={datePickerFrom}
          />
        </Box>
      </Panel>
      <SidebarLayout aside={<AuditLogsSidebarContainer />}>
        <Stack direction='column' height='100%' sx={{ overflowY: 'auto' }}>
          <Box width='100%' height='100%'>
            <VerticalSplitPane content={<AuditLogsTable />}>
              {({ className }) => (
                <AuditLogsDrawerContainer className={className} />
              )}
            </VerticalSplitPane>
          </Box>
        </Stack>
      </SidebarLayout>
    </Stack>
  );
};

export interface AuditLogsContainerProps {
  isUserAdminRole: boolean;
  isReadOnlyUser: boolean;
}

export default AuditLogsContainer;
